import React from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {decodeToken} from "react-jwt";
import Backend from "../backend/Backend";
import User from "../backend/User";

/**
 * Leitet bei aktivem Wartungsmodus jeden Seitenaufruf direkt auf /maintenance um.
 * Ausnahmen: der direkte Aufruf von /login (damit Admins sich anmelden können)
 * sowie die Wartungsseite selbst. Admins werden nie umgeleitet.
 *
 * Der Wartungsstatus wird einmalig über den öffentlichen /api/config-Endpunkt
 * abgefragt und für die Session gecacht. Bis der Status bekannt ist, werden die
 * eigentlichen Seiten NICHT gerendert — so feuern sie keine geschützten API-Calls,
 * die den Nutzer sonst (via 401) fälschlich auf /login umleiten würden.
 */
let maintenanceActive = null; // null = noch unbekannt, danach true/false
let maintenanceStatusPromise = null;

function fetchMaintenanceStatus() {
    if (maintenanceActive !== null) return Promise.resolve(maintenanceActive);
    if (!maintenanceStatusPromise) {
        maintenanceStatusPromise = Backend.getData('/api/config')
            .then(cfg => { maintenanceActive = !!(cfg && cfg.maintenance); return maintenanceActive; })
            .catch(() => { maintenanceActive = false; return false; });
    }
    return maintenanceStatusPromise;
}

// Admins sind vom Wartungsmodus ausgenommen, ebenso Impersonation-Sessions
// (ein Admin, der einen Benutzer impersoniert — Token trägt impersonatedBy).
// Sessions aus dem Kontoverbund (viaRelation) dagegen nicht: dahinter steht ein
// regulärer Benutzer. Muss zur Ausnahme in auth/maintenance.middleware.js passen.
function isExemptUser() {
    const token = User.getToken();
    if (!token) return false;
    const payload = decodeToken(token);
    if (!payload) return false;
    const adminImpersonation = payload.impersonatedBy && !payload.impersonatedBy.viaRelation;
    return !!(payload.isAdmin || adminImpersonation);
}

function MaintenanceGuard({children}) {
    const location = useLocation();
    const navigate = useNavigate();
    const path = location.pathname;
    // /login und /maintenance müssen immer erreichbar bleiben
    const exempt = path === '/login' || path === '/maintenance';

    const [ready, setReady] = React.useState(exempt || maintenanceActive === false);

    React.useEffect(() => {
        if (exempt) { setReady(true); return undefined; }

        let cancelled = false;
        fetchMaintenanceStatus().then(active => {
            if (cancelled) return;
            if (active && !isExemptUser()) {
                navigate('/maintenance', {replace: true});
            } else {
                setReady(true);
            }
        });
        return () => { cancelled = true; };
    }, [path, exempt, navigate]);

    if (exempt) return children;
    if (!ready) return null; // kurzer Moment, bis der Wartungsstatus feststeht
    return children;
}

export default MaintenanceGuard;
